import React from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { PAGE_ACCENTS, PageAccentName } from '../../theme/pageAccents';
import PageIconTile from './PageIconTile';
import { useMobileTopBarActions } from './MobileTopBarActionsContext';

interface PageHeaderProps {
  title: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  accent?: PageAccentName;
  titleAccessory?: React.ReactNode;
  actions?: React.ReactNode;
  mobileMode?: 'compact' | 'hidden';
}

/** Each page has its own drawing within the same quiet, cropped outline system. */
const motifArtwork: Record<PageAccentName, React.ReactNode> = {
  dashboard: <>
    <circle cx="292" cy="-18" r="134" />
    <circle cx="292" cy="-18" r="180" />
  </>,
  reports: <>
    <path d="M100 190V128" />
    <path d="M164 190V82" />
    <path d="M228 190V42" />
    <path d="M292 190V100" />
    <path d="M84 144 148 106 204 126 272 54 348 88" />
    <circle cx="148" cy="106" r="4" fill="currentColor" stroke="none" />
    <circle cx="204" cy="126" r="4" fill="currentColor" stroke="none" />
    <circle cx="272" cy="54" r="4" fill="currentColor" stroke="none" />
  </>,
  companies: <>
    <path d="M94 228V120C94 25 154-33 248-33S402 25 402 120V228" />
    <path d="M147 228V120C147 57 186 12 248 12S349 57 349 120V228" />
  </>,
  users: <>
    <path d="m132 61 69-37 89 40-36 80-87 30-35-113 122 83" />
    <g fill="currentColor" stroke="none">
      <circle cx="132" cy="61" r="4" /><circle cx="201" cy="24" r="4" />
      <circle cx="290" cy="64" r="4" /><circle cx="254" cy="144" r="4" />
      <circle cx="167" cy="174" r="4" />
    </g>
  </>,
  customers: <>
    <circle cx="244" cy="36" r="126" />
    <circle cx="172" cy="93" r="124" />
  </>,
  items: <>
    <path d="M246 -22 353 187 139 187Z" />
    <path d="M246 26 321 164 171 164Z" />
  </>,
  sales: <>
    <path d="M-8 184C73 170 88 63 170 88s91 30 169-90" />
    <path d="M28 222C105 207 120 101 192 124s91 28 166-90" />
  </>,
  debts: <>
    <path d="M70 177C84 95 131 47 195 47c71 0 116 48 129 128" />
    <path d="M111 19c8 78 56 129 123 129 53 0 87-30 118-89" />
  </>,
  expenses: <>
    <path d="M108-18 252 202" />
    <path d="M160-18 304 202" />
    <path d="M212-18 356 202" />
  </>,
  help: <>
    <path d="M100 190a190 190 0 0 1 190-190" />
    <path d="M156 190A134 134 0 0 1 290 56" />
    <path d="M212 190a78 78 0 0 1 78-78" />
  </>,
  faq: <>
    <path d="M133 25h126a23 23 0 0 1 23 23v59a23 23 0 0 1-23 23h-57l-28 23v-23h-41a23 23 0 0 1-23-23V48a23 23 0 0 1 23-23Z" />
    <path d="M204 91h84a20 20 0 0 1 20 20v49a20 20 0 0 1-20 20h-25l-24 20v-20h-35a20 20 0 0 1-20-20v-9" />
  </>,
  profile: <>
    <path d="M310 4C262-48 174-29 142 31c-36 67 8 121 69 134 64 14 119-24 126-82" />
    <path d="M294 35c-39-32-94-22-118 22-23 43 5 76 45 84 41 9 82-17 89-55" />
    <path d="M274 69c-22-18-50-10-61 9-10 20 3 38 23 42 20 4 40-8 43-28" />
  </>,
};

/** Quiet geometric outlines inspired by the public sign-in card. */
const PageHeaderMotif: React.FC<{ accent: PageAccentName }> = ({ accent }) => (
  <Box
    aria-hidden="true"
    sx={{
      position: 'absolute',
      right: { xs: -18, md: 0 },
      top: { xs: -16, md: -30 },
      width: { xs: 240, md: 320 },
      height: { xs: 165, md: 220 },
      display: { xs: 'none', md: 'block' },
      color: theme => alpha(PAGE_ACCENTS[accent][theme.palette.mode], theme.palette.mode === 'dark' ? 0.3 : 0.2),
      pointerEvents: 'none',
      '& svg': { display: 'block', width: '100%', height: '100%' },
    }}
  >
    <svg viewBox="0 0 320 220" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" focusable="false">
      {motifArtwork[accent]}
    </svg>
  </Box>
);

/** Shared title surface for application pages. */
const PageHeader: React.FC<PageHeaderProps> = ({ title, description, icon, accent, titleAccessory, actions, mobileMode = 'compact' }) => {
  const mobileTopBarActions = useMobileTopBarActions();

  return (
  <Paper
    component="section"
    variant="outlined"
    sx={{
      display: { xs: mobileMode === 'hidden' ? 'none' : 'flex', md: 'flex' },
      mb: { xs: 1.5, md: 2.5 },
      p: { xs: 1.25, md: 3 },
      width: { xs: '100%', md: 'calc(100% + 72px)' },
      ml: { xs: 0, md: '-36px' },
      minHeight: { xs: 68, md: 152 },
      boxSizing: 'border-box',
      alignItems: 'center',
      position: 'relative',
      overflow: 'hidden',
      borderRadius: { xs: 1.5, md: 2 },
      borderColor: theme => accent
        ? alpha(PAGE_ACCENTS[accent][theme.palette.mode], theme.palette.mode === 'dark' ? 0.32 : 0.24)
        : theme.palette.divider,
      backgroundColor: 'background.paper',
      background: theme => {
        const accentColor = accent ? PAGE_ACCENTS[accent][theme.palette.mode] : theme.palette.primary.main;
        return `linear-gradient(112deg, ${alpha(accentColor, theme.palette.mode === 'dark' ? 0.12 : 0.07)} 0%, ${alpha(accentColor, 0.015)} 62%, transparent 100%), ${theme.palette.background.paper}`;
      },
      boxShadow: { xs: 'none', md: '0 10px 26px rgba(32, 55, 44, .09)' },
    }}
  >
    {accent && <PageHeaderMotif accent={accent} />}
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      justifyContent="space-between"
      alignItems={{ xs: 'stretch', md: 'center' }}
      spacing={{ xs: 1, md: 2 }}
      useFlexGap
      flexWrap={{ md: 'wrap' }}
      sx={{ width: '100%', position: 'relative' }}
    >
      <Stack direction="row" alignItems="center" spacing={{ xs: 1, md: 1.5 }} sx={{ minWidth: 0, flex: { xs: '0 1 auto', md: '1 1 320px' } }}>
        {icon && (
          <PageIconTile
            icon={icon}
            accent={accent}
            onDarkSurface={Boolean(accent)}
            sx={{
              width: { xs: 36, md: 48 },
              height: { xs: 36, md: 48 },
              borderRadius: { xs: 1.25, md: 1.5 },
              '& svg': { fontSize: { xs: 20, md: 26 } },
            }}
          />
        )}
        <Box sx={{ minWidth: 0 }}>
          {mobileTopBarActions && titleAccessory ? titleAccessory : (
            <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap" sx={{ minWidth: 0 }}>
              <Typography
                component="h1"
                variant="h4"
                sx={{ minWidth: 0, overflowWrap: 'break-word', fontSize: { xs: 20, md: 32 }, fontWeight: 700, lineHeight: 1.2, letterSpacing: '-.025em' }}
              >
                {title}
              </Typography>
              {titleAccessory}
            </Stack>
          )}
          {description && (
            <Box sx={{ mt: 0.5, color: 'text.secondary', typography: 'body1', display: { xs: 'none', md: 'block' } }}>
              {description}
            </Box>
          )}
        </Box>
      </Stack>
      {actions && <Box sx={{
        minWidth: 0,
        maxWidth: '100%',
        flexShrink: 0,
        alignSelf: { xs: 'flex-end', md: 'center' },
        '& .MuiButton-root': { minHeight: { xs: 44, md: 48 }, width: 'auto' },
      }}>{actions}</Box>}
    </Stack>
  </Paper>
  );
};

export default PageHeader;
